import React, {forwardRef, useEffect, useState} from 'react';
import {useSwipeable} from 'react-swipeable';

import {Add as AddIcon} from '@mui/icons-material';
import {
    Collapse,
    Fab,
    List,
    ListItem,
    ListItemButton,
    ListItemText, Slide, Stack, Typography
} from '@mui/material';

import {
    ConfirmDialog,
    Dialog,
    Drawer,
    MenuHeader,
    PremiumLock,
    Puller,
    Text,
    useConfirmDialog} from 'components';
import {unTranslated, useTranslation} from 'hooks';
import {dateFormat} from 'lib/datefns';
import {Group, GroupIconButton} from 'module/group/index';
import {useGroup, useGroupDelete, useGroupSort} from 'module/group/useGroup';
import {useSheet} from 'module/sheet/useSheet';
import {useLicenseManager} from 'module/user';
import {useLayoutStore, useSnackbarStore} from 'store';

const styles = {
    actionButtonContainer: {
        display: 'flex',
        justifyContent: 'flex-start',
        padding: '16px',
        borderBottom: '1px solid',
        borderBottomColor: 'grey.darkest2',
        cursor: 'pointer',
        background: 'linear-gradient(to bottom, #1c1c1c, #111)',
        position: 'relative',
    },
    actionButton: {
        zIndex: 9,
        flexShrink: 0,
        backgroundColor: 'grey.darker',
        fontSize: '30px',
        marginRight: '16px',
        ':hover': {
            backgroundColor: 'grey.darker',
        },
    },
    addText: {
        alignItems: 'center',
    },
    listHolder: {
        padding: 0,
    },
    listItem: {
        width: '100%',
        borderBottom: '1px solid',
        borderBottomColor: 'grey.darkest2',
    },
    listItemActive: {
        backgroundColor: 'grey.darkest',
        borderBottom: '1px solid',
        borderBottomColor: 'grey.darkest2',
    },
    listContent: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '16px !important',
    },
    itemTextCollapse: {
        display: 'flex',
        flexGrow: 1,
        overflow: 'hidden',
        '&. MuiCollapse-wrapperInner': {
            display: 'flex',
            flexGrow: 1,
        },
    },
};

const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="down" ref={ref} {...props} />;
});

const GroupItem = ({group, isFirst, isLast, groupCount, editGroup, onToggle, onEdit, onDelete}) => {
    const translate = useTranslation();
    const {hasAccess} = useLicenseManager();

    const setShowSheetMenu = useLayoutStore(state => state.setShowSheetMenu);
    const setShowLoading = useLayoutStore(state => state.setShowLoading);

    const {setSelected: setSelectedGroup, selected} = useGroup();
    const {sheets, filterByGroup} = useSheet();

    const {sort: sortGroup, loading: loading2} = useGroupSort();

    const isActive = selected === group.id;
    const numSheets = filterByGroup(sheets, group.id).length;

    const txt = numSheets === 0 || numSheets > 1 ? 'board.sheet.sheets' : 'board.sheet.sheet';

    const handleSort = (group, direction) => {
        sortGroup(group, direction);
    };

    const handleSelect = (group) => {
        setSelectedGroup(group.id);
        setShowSheetMenu(true); // SheetMenu anzeigen
    };

    // Funktion, um das Klicken auf ein Gruppenelement zu verhindern, um das Menü zu schließen
    const preventClosingMenu = (event) => {
        event.stopPropagation();
    };
    
    const handlers = useSwipeable({
        onSwiped: (e) => onToggle(e, group),
        trackMouse: true,
    });

    useEffect(() => {
        setShowLoading(loading2);
    }, [loading2, setShowLoading]);

    return <ListItem sx={[isActive ? styles.listItemActive : styles.listItem]} onClick={preventClosingMenu}>
        <ListItemButton sx={styles.listContent} {...handlers}>
            <Collapse sx={styles.itemTextCollapse} in={group.id !== editGroup?.id} orientation="horizontal" collapsedSize={0} onClick={() => handleSelect(group)}>
                <ListItemText
                    primary={group.name}
                    secondary={`${dateFormat(group.created, 'dd.MM.yyyy, HH:mm')} | ${numSheets} ${translate(txt)}`}
                />
            </Collapse>
            <Puller onClick={(e) => onToggle(e, group)} />
            <Collapse in={group.id === editGroup?.id} orientation="horizontal" collapsedSize={0}>
                <Stack direction="row">
                    <GroupIconButton
                        icon="arrowup"
                        onClick={() => handleSort(group, 'up')}
                        disabled={isFirst}/>
                    <GroupIconButton
                        icon="arrowdown"
                        onClick={() => handleSort(group, 'down')}
                        disabled={isLast}/>
                    <GroupIconButton
                        icon="pencil"
                        onClick={() => onEdit(group)}
                    />
                    <GroupIconButton
                        icon="delete"
                        color="red.dark"
                        disabled={isActive || groupCount <= hasAccess('groups')?.limit?.min}
                        onClick={() => onDelete(group)}
                    />
                </Stack>
            </Collapse>
        </ListItemButton>
    </ListItem>;
};

const Menu = () => {
    const showInfo = useSnackbarStore((state) => state.show);
    const translate = useTranslation();
    const {hasAccess} = useLicenseManager();

    const showGroupMenu = useLayoutStore(state => state.showGroupMenu);
    const setShowGroupMenu = useLayoutStore(state => state.setShowGroupMenu);
    const setShowLoading = useLayoutStore(state => state.setShowLoading);
    const closeAll = useLayoutStore(state => state.closeMenu);

    const [editGroup, setEditGroup] = useState(null);
    const [showEdit, setShowEdit] = useState(false);

    const {groups, selected} = useGroup();

    const {del: deleteGroup, loading: loading1} = useGroupDelete();

    const handleEdit = (group) => {
        setEditGroup(group);
        setShowEdit(true);
    };

    const handleAdd = () => {
        setEditGroup(null);
        setShowEdit(true);
    };

    const handleDelete = (group) => {
        if (group.id !== selected) {
            deleteConfirm.show(group);
        } else {
            showInfo('board.group.validation.deletion_impossible', {severity: 'warning'});
        }
    };

    const deleteConfirm = useConfirmDialog(async (group) => {
        try {
            await deleteGroup(group);
            showInfo('board.group.action.delete_success');
        } catch (e) {
            showInfo(e.message, {severity: 'warning'});
        }
    },
    undefined,
    'board.group.action.confirm_delete_all');

    const closeEdit = () => {
        setShowEdit(false);
    };

    const closeMenu = () => {
        setShowGroupMenu(false);
    };

    const onSave = (group) => {
        if (editGroup === null) {
            setEditGroup(group);
        }

        closeEdit();
    };

    const toggleEditGroup = (e, group) => {
        if (e.dir === 'Left') {
            setEditGroup(group);
        } else if (e.dir === 'Right') {
            setEditGroup(null);
        } else {
            setEditGroup(editGroup === group ? null : group);
        }
    };

    useEffect(() => {
        setShowLoading(loading1);
    }, [loading1, setShowLoading]);

    return (
        <Drawer open={showGroupMenu.show} onClose={closeMenu}>
            <ConfirmDialog {...deleteConfirm} />
            {/* Menu Header */}
            <MenuHeader
                text={<Text>{unTranslated('Themen')}</Text>}
                handleBackButton={closeMenu}
                handleClose={closeAll}
            />
            {hasAccess('groups')?.limit?.max > groups.length && <Stack sx={styles.actionButtonContainer} onClick={handleAdd} direction="row" alignItems="center">
                <Fab disableRipple sx={styles.actionButton}><AddIcon color="white" /></Fab>
                <Typography><Text>board.group.action.create</Text></Typography>
            </Stack>}

            {hasAccess('groups')?.limit?.max <= groups.length && <Stack sx={styles.actionButtonContainer} onClick={() => console.log('LINK TO PREMIUM PAGE')} direction="row" alignItems="center">
                <PremiumLock text={translate('board.packages.premium.groups')} />
                <Stack direction="row" sx={styles.addText}>
                    <Fab disableRipple sx={styles.actionButton}><AddIcon color="white" /></Fab>
                    <Typography><Text>board.group.action.create</Text></Typography>
                </Stack>
            </Stack>}

            {showEdit && <Dialog
                maxWidth={false}
                pt={0}
                open={showEdit}
                TransitionComponent={Transition}
                handleClose={closeEdit}
                title={translate('board.group.action.create')}
            >
                <Group group={editGroup} onSave={onSave} onCancel={closeEdit}/>
            </Dialog>}

            <List sx={styles.listHolder}>
                {groups.map((group, idx) => {
                    return <GroupItem 
                        key={group.id} 
                        group={group}
                        groupCount={groups.length}
                        isFirst={idx===0} 
                        isLast={idx === groups.length - 1} 
                        editGroup={editGroup} 
                        onToggle={toggleEditGroup} 
                        onEdit={handleEdit} 
                        onDelete={handleDelete}/>;
                })}
            </List>
        </Drawer>
    );
};

export default Menu;